import { throttle } from "lodash";
import { storeToRefs } from "pinia";
import { Ref, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "./draft-manager";
import { useEditManager } from "./edit-manager";
import { useEntityNameManager } from "./entity-name-manager";

export type Role = types.Role;

// Uses the current route to determine what the 'role' should be and fetches
// the role description for you.
export const useRoleStore = defineEntityStore("role", () => {
    const { organization } = useOrganizationManager();
    const { stek } = useStekManager();

    const role = ref<Role | undefined>(undefined);
    const router = useRouter();
    const syncStore = useSyncStore();
    const entityId = computed(() => role.value?.identity);
    const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.ROLE,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.ROLE);

    const { role: roleInfo } = useCurrentEntityManager();

    const isFetching = ref(false);
    const controller = ref(new AbortController());
    const {
        isModified: isNameModified,
        reset: resetName,
        persist: persistName,
    } = useEntityNameManager(role);

    async function updateRole(force = false) {
        const roleId = roleInfo.value?.identity;

        // In case the same role is selected, return early.
        if (!force && role.value && role.value.identity === roleId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!roleId) {
            description.value = undefined;
            role.value = undefined;
            return;
        }

        isFetching.value = true;
        description.value = undefined;
        role.value = undefined;

        try {
            const { data, response } =
                await api.startDomainRoleMgtGetRoleStartPost({
                    body: {
                        role_id: `${roleId}`,
                    },
                    signal: controller.value.signal,
                });
            if (!response.ok) {
                if (response.status === 404) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value?.name,
                            stekName: stek.value?.name,
                        },
                        query: {
                            type: "role",
                            id: roleId,
                        },
                    });
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            role.value = data;

            // Fetch description while still in the try block
            await updateDescription(roleId);
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isFetching.value = false;
        }
    }
    watch(
        () => roleInfo.value?.identity,
        () => updateRole(),
        { immediate: true }
    );

    const updateDescription = async (currentRoleId?: string | undefined) => {
        if (!description.value && role.value) {
            try {
                const descriptionResult = await getDescriptionArtefact(
                    role.value,
                    "roleDescription",
                    controller.value.signal
                );

                // Check if role has changed during the async operation
                if (!role.value || role.value.identity !== currentRoleId) {
                    console.debug(
                        "Role changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            }
        }
    };

    const isModified = computed(() => {
        if (isFetching.value) {
            return false;
        }
        return Boolean(isNameModified.value || description.value?.isModified);
    });

    const queueSave = async () => {
        if (!isModified.value || !role.value) {
            return;
        }

        const updateContent: types.StartDomainRoleMgtUpdateRoleStartPostData = {
            body: {
                role_id: role.value.identity,
            },
            url: "/domain/role_mgt/update_role/start",
        };

        if (isNameModified.value) {
            updateContent.body.name = role.value.name;
        }
        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            updateContent.body.role_id &&
            Object.keys(updateContent.body).length === 1
        ) {
            // Only role_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.ROLE,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
        if (isNameModified.value) {
            persistName();
        }
    };
    const reset = async () => {
        await description.value?.resetContent();
        resetName();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.ROLE &&
                identity.entityId === role.value?.identity
            ) {
                updateRole(true);
            }
        }
    );

    return {
        role,
        description,
        isFetching,
        isModified,
        reset,
    };
});

export const useRoleManager = () => storeToRefs(useRoleStore());
